import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Drawer, Form, Image, Input, message, Select, Space, Tag } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import ReactTimeago from 'react-timeago';
import { useBomModelServicePutBackendv2Bommodel } from '../../backendapi/queries';
import { BomModelConfigurationPointViewDTO, BomModelPlacementPointDTO, BomModelService, BomModelSurfaceDTO, BomModelViewDTO } from '../../backendapi/requests';
import { ConfigContext } from '../../contexts/ConfigContext';
import { BASE_PATH } from '../../Environment';
import TextArea from 'antd/es/input/TextArea';
import BomModelDesigner from './BomModelDesigner';

const EditBomModel: React.FC<{ modelId?: number, isOpen: boolean, onClose: () => void }> = ({ modelId, isOpen, onClose }) => {
    const { currentClient } = useContext(ConfigContext);
    const [isEditorOpen, setIsEditorOpen] = useState(false);

    const { mutateAsync: updateModel, isPending } = useBomModelServicePutBackendv2Bommodel();

    const [form] = Form.useForm<BomModelViewDTO>();
    const [messageApi, contextHolder] = message.useMessage();

    const title = Form.useWatch('title', form);

    const [bomModel, setBomModel] = useState<BomModelViewDTO | undefined>(undefined);
    const [points, setPoints] = useState<BomModelConfigurationPointViewDTO[]>([]);

    const handleEditorSave = useCallback((configuration: string, placements: BomModelPlacementPointDTO[], surfaces: BomModelSurfaceDTO[]) => {
        form.setFieldValue("configuration", configuration);
        form.setFieldValue("surfaces", surfaces);
        form.setFieldValue("placementpoints", placements);

        setIsEditorOpen(false);
    }, []);

    const onSubmit = () => {
        form.submit();
    }

    const onFinish = () => {
        if (modelId) {
            const values = form.getFieldsValue(true) as BomModelViewDTO;
            values.clientId = currentClient.id;
            updateModel({ key: modelId, requestBody: values })
                .then(() => {
                    messageApi.success("Bom Model updated");
                    onClose();
                }).catch(reason => {
                    messageApi.error(JSON.stringify(reason));
                });
        }
    };

    useEffect(() => {
        if (modelId && isOpen) {
            form.resetFields();
            BomModelService.getBackendv2Bommodel1(modelId).then(bomModel => {
                setBomModel(bomModel);
                form.setFieldsValue({ ...bomModel, metadata: [...bomModel.metadata].sort((a, b) => ((a.locked ? 1 : 0) - (b.locked ? 1 : 0)) || a.name.localeCompare(b.name)) });

                BomModelService.putBackendv2BommodelConfiguration(modelId, []).then(state => {
                    setPoints(state.points);
                });
            });
        }
    }, [modelId, isOpen]);

    return (
        <>
            {contextHolder}

            <Drawer
                title={`Edit ${title}`}
                size={720}
                onClose={onClose}
                open={isOpen}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={onSubmit} type="primary" loading={isPending}>
                            Submit
                        </Button>
                    </Space>
                }
            >
                <Form
                    name="basic"
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}>

                    <Form.Item label="Thumbnail">
                        <Space orientation='vertical'>
                            <Image placeholder="" preview={{src: `${BASE_PATH}/bommodel/${modelId}/image`}} width={300} src={`${BASE_PATH}/bommodel/${modelId}/thumbnail`} />
                        </Space>
                    </Form.Item>

                    <Form.Item name="isEnabled" valuePropName="checked">
                        <Checkbox>Enabled</Checkbox>
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='title' label="Title">
                        <Input />
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='name' label="Name">
                        <Input />
                    </Form.Item>

                    <Form.Item name='configuration' label="Configuration">
                        <TextArea rows={6} />
                    </Form.Item>

                    <Form.Item>
                        <Button onClick={() => setIsEditorOpen(true)}>Change configuration</Button>
                        {bomModel && <BomModelDesigner isOpen={isEditorOpen} modelId={bomModel.id} onClose={() => setIsEditorOpen(false)} onSave={handleEditorSave} />}
                    </Form.Item>

                    <Form.Item label='Surfaces'>
                        {points.filter(e => e.type == "surface").map((s) => (
                            <Space key={s.name} style={{ display: 'flex', padding: 4 }} align="baseline">
                                <span style={{ display: 'inline-block', width: 150 }}>{s.label}</span>
                                {s.asset && <Tag color="green">{s.asset.title}</Tag>}
                                {s.isExternal && <Tag>External</Tag>}
                            </Space>
                        ))}
                    </Form.Item>

                    <Form.Item label='Placement points'>
                        {points.filter(e => e.type == "model").map((s) => (
                            <Space key={s.name} style={{ display: 'flex', padding: 4 }} align="baseline">
                                <span style={{ display: 'inline-block', width: 150 }}>{s.label}</span>
                                {s.asset && <Tag color="green">{s.asset.title}</Tag>}
                                {s.isExternal && <Tag>External</Tag>}
                            </Space>
                        ))}
                    </Form.Item>

                    <Form.Item name='tags' label="Tags">
                        <Select mode='tags' options={[]} />
                    </Form.Item>

                    <Form.Item label='Metadata'>
                        {bomModel?.timestampMetadataImport && <p>Metadata imported from DAM <ReactTimeago date={bomModel?.timestampMetadataImport ?? '1970-1-1'} /></p>}
                        <Form.List name="metadata">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex' }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'name']}
                                                style={{ width: 180 }}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="name" disabled={form.getFieldValue(["metadata", name, "locked"])} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                style={{ width: 360 }}
                                                name={[name, 'value']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="value" disabled={form.getFieldValue(["metadata", name, "locked"])} />
                                            </Form.Item>
                                            {form.getFieldValue(["metadata", name, "locked"]) !== true && <MinusCircleOutlined onClick={() => remove(name)} />}
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Add metadata
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>
                </Form>
            </Drawer>
        </>

    );
};

export default EditBomModel;